import React, { useContext, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useMediaQuery } from 'react-responsive';
import { Form, Formik } from 'formik';
import Alert from '@material-ui/lab/Alert';

import ContactActionBar from './ContactActionBar/ContactActionBar';
import ContactMyRequest from './ContactMyRequest/ContactMyRequest';
import ContactPersonalDetails from './ContactPersonalDetails/ContactPersonalDetails';
import { ExtraStyleType } from 'components/Common/Formik/FieldCustom/FieldCustom';
import ScrollToFieldError from 'components/Common/Formik/ScrollToFieldError/ScrollToFieldError';
import FormikErrorsGtmLogger from 'components/Common/FormikErrorsGtmLogger/FormikErrorsGtmLogger';
import { ScrollToTop } from 'components/Common/ScrollToTop/ScrollToTop';
import TurnstileFormikRow, {
  TurnstileRef,
} from 'components/Common/Cloudflare/TurnstileFormikRow';
import Loading from 'components/Common/Loading/Loading';
import RequiredFields from 'components/Common/RequiredFields/RequiredFields';
import { userContext } from 'contexts/userContext';
import useContact from 'hooks/useContact';
import useCurrentCountry from 'hooks/useCurrentCountry';
import useGtmPageView from 'hooks/gtm/useGtmPageView';
import { formSubjects } from 'model/ContactFormFields';
import validationSchema from 'schemas/ContactValidationSchema';
import { apiUrls } from 'services/api-urls';
import { fetcherWithBody } from 'services/fetcher';
import { replacePhoneNumberWithoutSpace } from 'services/formatter';
import { gtmAfiEventLab } from 'services/gtm';
import { GTM_PAGE_OTHER } from 'settings/gtm/gtmPageView';

import styles from './ContactPage.module.scss';

type Message = {
  message: string;
  severity: 'success' | 'error';
};

export default function ContactPage(): JSX.Element {
  const { t } = useTranslation(['contactPage', 'translation']);
  const location = useLocation();
  const { gtmUserCookie } = useContext(userContext);
  const { initialValues } = useContact();
  const { isUk } = useCurrentCountry();
  const [messages, setMessages] = useState<Message[]>();
  const [isLoading, setLoading] = useState(false);
  const turnstile = useRef<TurnstileRef>();
  const isTurnstileDesktop = useMediaQuery({ minWidth: '359px' });
  const extraStyle: ExtraStyleType = {
    fieldContainer: styles.fieldContainer,
    label: styles.fieldLabel,
  };

  useGtmPageView(GTM_PAGE_OTHER, `Afibel - ${t('CONTACT_META_TITLE')}`);

  async function onSubmit(values: typeof initialValues) {
    setMessages(undefined);
    setLoading(true);

    const data: RequestDataType = {
      ...values,
      landLine: replacePhoneNumberWithoutSpace(values.landLine || ''),
    };

    try {
      const uri = apiUrls.getContactSubmit();
      const response = (await fetcherWithBody(uri, data, true)) as Response;

      if (response.ok) {
        setMessages([
          { message: t('LABEL_SENT_CONTENT_SUCCESS'), severity: 'success' },
        ]);

        gtmAfiEventLab(
          location.pathname,
          'afiEventLab',
          'Contact',
          'Envoi message',
          gtmUserCookie,
          formSubjects.find(v => v.value === values.subject)?.label
        );

        return;
      }

      const json = await response.json();

      if (json.error) {
        setMessages([{ message: json.error, severity: 'error' }]);
      } else if (json.errors && Object.keys(json.errors).length > 0) {
        setMessages(
          Object.values(json.errors).map(message => ({
            message,
            severity: 'error',
          })) as Message[]
        );
      } else {
        // TODO: Generate default error message
      }
    } catch {
      // TODO: Generate default error message
    } finally {
      turnstile.current?.reset();
      setLoading(false);
    }
  }

  return (
    <div className={styles.container}>
      <Helmet>
        <title>
          {t('META_TITLE_TEMPLATE', {
            title: t('CONTACT_META_TITLE'),
            ns: 'translation',
          })}
        </title>
        <meta
          name="description"
          content={t('LABEL_CONTACT_META_DESCRIPTION')}
        />
      </Helmet>
      <h1>{t('LABEL_CONTACT_US')}</h1>
      {messages?.length && (
        <>
          <ScrollToTop />
          {messages.map(({ message, severity }, i) => (
            <Alert key={i} severity={severity}>
              {message}
            </Alert>
          ))}
        </>
      )}
      <div className={styles.informations}>
        <p>{t('LABEL_ASK_QUESTION')}</p>
        {isUk && (
          <p>
            {t('LABEL_CALL_US')}
            <br />
            {t('LABEL_ORDER_LINE')}*
            <br />
            {t('LABEL_CUSTOMER_CARE')}
            <br />
            {t('LABEL_POST')}
            <br />
            {t('LABEL_COST')}
          </p>
        )}
      </div>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema(isUk, t)}
        onSubmit={onSubmit}
        enableReinitialize={true}
      >
        <Form noValidate>
          <FormikErrorsGtmLogger page_type="autre" form_type="contact" />
          <ContactPersonalDetails extraStyle={extraStyle} />
          <ContactMyRequest extraStyle={extraStyle} />
          <TurnstileFormikRow
            classes={{ errorMessage: styles.captchaErrorMessage }}
            turnstileRef={turnstile}
            turnstileProps={{
              className: styles.captchaField,
              options: {
                action: 'contact_page',
                size: isTurnstileDesktop ? 'normal' : 'compact',
              },
            }}
          />
          <ContactActionBar />
          <RequiredFields />
          <ScrollToFieldError />
        </Form>
      </Formik>
      {isLoading && <Loading loading />}
    </div>
  );
}

type RequestDataType = {
  turnstileToken: string;
  civility: string;
  civilityNumber: string;
  lastName: string;
  firstName: string;
  email: string;
  nameWay: string;
  nameComplement: string;
  nameAddress: string;
  locality: string;
  postCode: string;
  city: string;
  country: string;
  landLine: string;
  customerNumber: string;
  orderNumber: string;
  subject: string;
  message: string;
};
